import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MediaService } from '@exl-ng/mulo-core';
import { ProfileProjectsService } from './profile-projects.service';
import { FacetValue } from '../../shared/interfaces/list-data-params';
import {
    GrowInAnimation,
    MuloNavLink,
    OpacityInAnimation,
    OpacityOutAnimation,
    BreadcrumbsNavComponent,
    VerticalTabsDirective,
} from '@exl-ng/mulo-common';
import { UrlUtils } from '../../shared/utils/url.utils';
import {
    ActivatedRoute,
    Router,
    RouterLinkActive,
    RouterLink,
    RouterOutlet,
} from '@angular/router';
import { ReplaySubject, takeUntil } from 'rxjs';
import { ProfileService } from '../profile.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { SEOService } from '../../core/seo.service';
import { ProfileDataService } from '../profile-data.service';
import { I18nService } from '../../core/i18n.service';
import { AutomationClassDirective } from '../../shared/directives/tests/automation-tests-class.directive';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { AsyncPipe } from '@angular/common';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { NavMainLayoutComponent } from '@exl-ng/mulo-views';

@Component({
    selector: 'esp-profile-projects',
    templateUrl: './profile-projects.component.html',
    styleUrls: ['./profile-projects.component.scss'],
    animations: [GrowInAnimation, OpacityInAnimation, OpacityOutAnimation],
    imports: [
        NavMainLayoutComponent,
        ContentLoaderModule,
        BreadcrumbsNavComponent,
        MatTabNav,
        AutomationClassDirective,
        VerticalTabsDirective,
        MatTabLink,
        RouterLinkActive,
        RouterLink,
        RouterOutlet,
        AsyncPipe,
        TranslateModule,
        MatTabNavPanel,
    ],
})
export class ProfileProjectsComponent implements OnInit, OnDestroy {
    media = inject(MediaService);
    profileProjectsService = inject(ProfileProjectsService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private profileService = inject(ProfileService);
    profileDataService = inject(ProfileDataService);
    private translate = inject(TranslateService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private SEOService = inject(SEOService);
    i18nService = inject(I18nService);

    isLoading = true;
    institutionCode: string;
    _onDestroy = new ReplaySubject<void>();
    tabDirection: string;

    ngOnInit(): void {
        this.institutionCode = UrlUtils.getParam('institution');
        this.profileProjectsService.getFacetsProjects();
        this.profileProjectsService.facetsChange$
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.navigateToElementInList();
                this.isLoading = false;
            });

        this.route.data.subscribe((res) => {
            //  deep link to the direct tab - need to wait until the name updates
            this.profileService.nameChange$
                .pipe(takeUntil(this._onDestroy))
                .subscribe((res) => {
                    this.changeDocumentTitle(res);
                });
        });
    }

    navigateToElementInList() {
        if (!this.route.snapshot.firstChild) {
            this.navigateToFirstResourceTypeInDisplay();
        } else {
            const id = this.route.snapshot.firstChild.params.id;
            const index = this.profileProjectsService.facets.findIndex(
                (facet: FacetValue) => facet.value === id,
            );
            if (index !== -1) {
                this.profileProjectsService.selectedIndex$.next(index);
                this.navigateToFacet(id);
            } else {
                this.navigateToFirstResourceTypeInDisplay();
            }
        }
        this.changeDocumentTitle(this.profileDataService.displayName.value);
    }

    navigateToFirstResourceTypeInDisplay() {
        const facet = this.profileProjectsService.facets?.[0];
        if (facet) {
            this.navigateToFacet(facet.value);
            this.profileProjectsService.selectedIndex$.next(0);
        }
    }

    updateSelectedIndex(facet: FacetValue) {
        const index = this.profileProjectsService.facets.findIndex(
            (item: FacetValue) => item.value === facet.value,
        );
        this.profileProjectsService.selectedIndex$.next(index);
        this.changeDocumentTitle(
            this.profileDataService.displayName.value,
            facet.label,
        );
    }

    onMobileFacetChange(selectedMobileFacet: MuloNavLink) {
        this.navigateToFacet(selectedMobileFacet.name);
    }

    navigateToFacet(facet: string) {
        this.router.navigate([facet], {
            queryParams: { institution: this.institutionCode },
            relativeTo: this.route,
            replaceUrl: true,
        });
    }

    changeDocumentTitle(userName, facet?: string) {
        const code = this.getDocumentTitle();
        const titleSuffix = this.translate.instant(
            'research.projects.facet.' + facet,
        );

        this.translate
            .get(code, {
                value0: userName,
                value1: this.configurationHandlerService.mainInstitutionName(),
                value2: titleSuffix,
            })
            .subscribe((res) => {
                this.SEOService.updateTitle(res);
            });
    }

    getDocumentTitle() {
        let code = '';
        if (this.profileService.isPublicPage) {
            code = 'research.public.profile.projects.tab.document.title';
        } else {
            code = 'research.profile.projects.tab.document.title';
        }
        return code;
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }
}
